import at from "lodash/at";
import { useField, useFormikContext } from "formik";
import {
  FormControl,
  MenuItem,
  TextField,
  InputAdornment,
  Checkbox,
  ListItemText,
  Typography,
  ListSubheader,
  Button,
  Stack,
  Box,
  Theme,
  SxProps,
} from "@mui/material";

import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { GroupedActivityTag } from "../../../types/groupedActivityTag";

type MultiSelectTagFieldProps = {
  name: string;
  label: string;
  data: GroupedActivityTag[];
  openSelect?: boolean;
  onClose?: () => void;
  isDisabled: boolean;
  sx: SxProps<Theme>;
  isFullWidth: boolean;
};
export default function MultiSelectTagField({
  name,
  label,
  data,
  openSelect,
  isDisabled,
  isFullWidth,
  onClose,
  sx,
}: MultiSelectTagFieldProps) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  // const { ...rest } = props;
  const [field, meta] = useField(name);
  const { value: selectedValue } = field;
  const [touched, error] = at(meta, "touched", "error");
  const isError = touched && error && true;
  const { setFieldValue } = useFormikContext();
  const [expandedHeaders, setExpandedHeaders] = useState<
    Record<string, boolean>
  >({});

  const handleHeaderClick = (itemValue: string) => {
    setExpandedHeaders((prev) => ({
      ...prev,
      [itemValue]: !prev[itemValue], // Toggle the collapse state
    }));
  };

  const displayedOptions = useMemo(() => {
    return data
      .map((item) => {
        // Filter children based on searchText
        const filteredChildren = item.children.filter((child) =>
          t(child.value).toLowerCase().includes(searchText.toLowerCase())
        );

        // Check if the parent matches the searchText or if any children match
        if (
          // t(item.value).toLowerCase().includes(searchText.toLowerCase()) ||
          filteredChildren.length > 0
        ) {
          return {
            ...item,
            children: filteredChildren, // Include only the matching children
          };
        }
        return null;
      })
      .filter((item) => item !== null); // Remove null values
  }, [searchText, data]);

  // {...rest}
  return (
    <FormControl
      sx={sx}
      fullWidth={isFullWidth}
      disabled={isDisabled}
      error={isError}
    >
      <TextField
        //
        select
        fullWidth
        label={t(label)}
        error={isError}
        helperText={isError ? t(error) : ""}
        sx={{
          "& .MuiSelect-icon": { right: "40px !important" },
        }}
        SelectProps={{
          open: openSelect && openSelect,
          onOpen: () => {
            if (openSelect) {
              if (!openSelect) {
                //rest.onOpen();
              }
            }
          },
          onClose: () => {
            if (openSelect) {
              if (onClose) {
                onClose();
              }
            }
            setSearchText("");
          },
          multiple: true,
          value: selectedValue,
          MenuProps: {
            autoFocus: false,
            PaperProps: {
              style: {
                minHeight: "90%", // TODO auto adjust
              },
            },
          },
          renderValue: (selected) => {
            const selectedArray = selected as string[];
            if (selectedArray.length === 0) return t("None selected");
            if (selectedArray.length <= 2) {
              return selectedArray.map((val) => t(val)).join(", ");
            }
            return `${selectedArray.length} ${t("selected")}`;
          },
        }}
        // InputProps={{
        //   endAdornment: (
        //     <>
        //       {infoMsg && (
        //         <InputAdornment position="end">
        //           <Tooltip title={infoMsg}>
        //             <HelpIcon style={{ cursor: "default" }} />
        //           </Tooltip>
        //         </InputAdornment>
        //       )}
        //     </>
        //   ),
        // }}
      >
        <ListSubheader>
          <TextField
            size="small"
            // Autofocus on textfield
            autoFocus
            placeholder="Type to search..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Escape") {
                // Prevents autoselecting item while typing (default Select behaviour)
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        {displayedOptions.map((item) => {
          return [
            <Button
              sx={{ justifyContent: "flex-start", textTransform: "none" }}
              fullWidth
              key={`header-${item.header}`}
              onClick={() => handleHeaderClick(item.header)} // Toggle collapse state
            >
              <Stack
                marginRight={1}
                marginLeft={1}
                direction={"row"}
                width={"100%"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Typography fontWeight={"bold"}>{t(item.header)}</Typography>
                <Box>
                  {expandedHeaders[item.header] || false ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </Box>
              </Stack>
            </Button>,

            <Collapse
              key={`collapse-${item.header}`}
              in={expandedHeaders[item.header] || false}
              orientation="vertical"
            >
              {item.children.length > 0 &&
                item.children.map((v) => (
                  <MenuItem
                    onClick={() => {
                      const newValue = field.value.includes(v.value)
                        ? field.value.filter(
                            (value: string) => value !== v.value
                          )
                        : [...field.value, v.value];
                      setFieldValue(field.name, newValue);
                    }}
                    sx={{ marginLeft: 3 }}
                    key={v.value}
                    value={v.value}
                  >
                    <Checkbox checked={field.value.indexOf(v.value) > -1} />
                    <ListItemText primary={t(v.value)} />
                  </MenuItem>
                ))}
            </Collapse>,
          ];
        })}
      </TextField>
    </FormControl>
  );
}
